import { useRef } from 'react'

import { Box, Button, Section } from '@factorialco/deprecated-design-system'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdateContactDataDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EmployeeContactForm from 'modules/employees/lib/forms/Personal/EmployeeContactForm'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly: boolean
}

interface WrapperProps {
  employee: EmployeeBlocksEmployeeFragment
  readonly: boolean
  isCurrent: boolean
}

export const ContactBlock = observer(({ employee, readonly }: Props) => {
  const { addEmergencyContact, addEmergencyPhoneNumber } = trackingEvents.employee.personal
  const [updateContactData] = useMutation(EmployeeBlocksUpdateContactDataDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
    update: (cache, result) => {
      const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
      if (updatedEmployee) {
        cache.writeFragment({
          id: cache.identify(employee),
          fragment: EmployeeBlocksEmployeeFragmentDoc,
          fragmentName: 'EmployeeBlocksEmployee',
          data: {
            ...employee,
            contactName: updatedEmployee.contactName,
            contactNumber: updatedEmployee.contactNumber,
          },
        })
      }
    },
  })
  const form = useRef(
    new EmployeeContactForm({
      contact_name: employee.contactName ?? '',
      contact_number: employee.contactNumber ?? '',
    })
  )
  const contactForm = form.current

  const trackEvent = () => {
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(contactForm),
      legal_entity_country: employee.legalEntity?.country,
      layout_version: 'v1',
    })
  }

  const onSubmit = async () => {
    trackEvent()

    const result = await updateContactData({
      variables: {
        employeeId: employee.id,
        contactName: contactForm.get('contact_name').value,
        contactNumber: contactForm.get('contact_number').value,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.contact.update_contact_data_error'),
        })
      }
    })

    if (handled.success) {
      contactForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.contact.update_contact_data'),
      })
    }

    return handled
  }

  return (
    <FormComponent onSubmit={onSubmit}>
      <FormRow>
        <Text
          label={i18n.t('employee_form.contact_name')}
          field={contactForm.get('contact_name')}
          placeholder={i18n.t('employee_form.contact_name_placeholder')}
          readonly={readonly}
          onBlur={() => track(addEmergencyContact)}
        />
      </FormRow>

      <FormRow>
        <Text
          label={i18n.t('employee_form.contact_number')}
          field={contactForm.get('contact_number')}
          placeholder={i18n.t('employee_form.contact_number_placeholder')}
          readonly={readonly}
          onBlur={() => track(addEmergencyPhoneNumber)}
        />
      </FormRow>

      {contactForm.isDirty && (
        <FormRow footer>
          <Button submit>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </FormComponent>
  )
})

const Contact = (props: WrapperProps) => {
  return (
    <Section
      illustration="user-health"
      title={i18n.t('employee_form.emergency_contact.title')}
      description={i18n.tCurrent('employee_form.emergency_contact.description', props.isCurrent)}
    >
      <Box
        gap="s24"
        background="grey200"
        padding="s32"
        width="s360"
        alignSelf="flexEnd"
        borderRadius={{
          all: 'abs004',
        }}
      >
        <ContactBlock {...props} />
      </Box>
    </Section>
  )
}

export default observer(Contact)
